/**
 * 常量定义
 */

// 钱包类型
export const WALLET_TYPES = {
  METTAKERSK: 'MetaMask',
  WALLETCONNECT: 'WalletConnect',
  OKX: 'OKX',
  UNKNOWN: 'Unknown'
} as const

// 错误代码
export const ERROR_CODES = {
  WALLET_NOT_CONNECTED: 'WALLET_NOT_CONNECTED',
  USER_REJECTED: 'USER_REJECTED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  INVALID_ADDRESS: 'INVALID_ADDRESS',
  TRANSACTION_FAILED: 'TRANSACTION_FAILED'
} as const

// 本地存储 Key
export const STORAGE_KEYS = {
  WALLET_STATE: 'wallet_state',
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_INFO: 'user_info'
} as const

// 默认配置
export const DEFAULT_CONFIG = {
  BALANCE_REFRESH_INTERVAL: 30000, // 余额刷新间隔（毫秒）
  CONNECTION_TIMEOUT: 15000, // 连接超时时间（毫秒）
  MAX_RETRY_COUNT: 3 // 最大重试次数
} as const

/** 全站统一用户头像（蓝色默认头像） */
export const DEFAULT_USER_AVATAR = '/static/image/common/default-avatar.webp'

// 默认交易对（保证页面不报错的兜底数据）
// - symbol: 交易对符号 (XXXUSDT)
// - displaySymbol: 显示格式 (XXXUSDT)
// - binanceSymbol: 币安WebSocket格式 (XXXUSDT)
// - pricePrecision: 价格精度
export const DEFAULT_MARKETS = [
  { symbol: 'BTCUSDT', displaySymbol: 'BTC/USDT', pricePrecision: 2, binanceSymbol: 'BTCUSDT', name: 'BTC', icon: '/static/image/markets/btc-v3.webp' },
  { symbol: 'CLUSDT', displaySymbol: 'CL/USDT', pricePrecision: 2, binanceSymbol: 'CLUSDT', name: 'CL', icon: '/static/image/markets/cl-v3.webp' },
  { symbol: 'XAUUSDT', displaySymbol: 'XAU/USDT', pricePrecision: 2, binanceSymbol: 'XAUUSDT', name: 'XAU', icon: '/static/image/markets/xau-v3.webp' },
] as const

// 默认币安交易对符号列表
export const DEFAULT_BINANCE_SYMBOLS = DEFAULT_MARKETS.map(m => m.binanceSymbol)

export function normalizeMarketSymbol(symbol?: string | null): string {
  return String(symbol || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
}

/** 平台折线：1s / 1m；其余周期走币安 U 本位 K 线 */
export function isPlatformChartInterval(interval?: string | null): boolean {
  const v = String(interval || '').toLowerCase().trim()
  return v === '1s' || v === '1m'
}

/**
 * 实际喂给图表的周期：
 * - 1s/1m → 平台（PLATFORM 仅支持这些）
 * - 其它 → 币安 U 本位（fapi），目录内品种实测可用
 */
export function resolveChartInterval(
  _symbol?: string | null,
  uiInterval?: string | null
): string {
  return String(uiInterval || '1s').toLowerCase().trim() || '1s'
}

/** 新 AI 只开放 BTC / XAU。CL、ETH 保留普通交易和历史查询。 */
export const AI_PREDICTION_SYMBOLS = ['BTCUSDT', 'XAUUSDT'] as const
/** 默认品种，兼容旧调用 */
export const AI_PREDICTION_ALLOWED_SYMBOL = 'BTCUSDT'
export const AI_PREDICTION_ALLOWED_UI_TIMEFRAME = '1s'
export const AI_PREDICTION_ALLOWED_API_INTERVAL = '1m'

const AI_SYMBOL_ALIASES: Record<string, (typeof AI_PREDICTION_SYMBOLS)[number]> = {
  BTC: 'BTCUSDT',
  BTCUSDT: 'BTCUSDT',
  XAU: 'XAUUSDT',
  XAUUSDT: 'XAUUSDT',
}

export function normalizeAiMarketSymbol(symbol?: string | null): string {
  return String(symbol || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
}

/** 合法则返回目录 symbol，否则 null。不把 ETH 映射成 BTC。 */
export function resolveAiPredictionSymbol(symbol?: string | null): string | null {
  return AI_SYMBOL_ALIASES[normalizeAiMarketSymbol(symbol)] || null
}

export function isAiPredictionAllowedSymbol(symbol?: string | null): boolean {
  return resolveAiPredictionSymbol(symbol) != null
}

export function isAiPredictionAllowedTimeframe(timeframe?: string | null): boolean {
  const v = String(timeframe || '').toLowerCase().trim()
  return v === AI_PREDICTION_ALLOWED_UI_TIMEFRAME || v === AI_PREDICTION_ALLOWED_API_INTERVAL
}

/** 滚动下单六周期（接口枚举）；UI「1分钟」可用 1s / 1m */
export const ROLLING_ORDER_INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d'] as const
export type RollingOrderInterval = (typeof ROLLING_ORDER_INTERVALS)[number]

/** 普通滚动单可选 UI 周期（含 1s → 接口 1m） */
export const ORDERABLE_TIMEFRAMES = ['1s', '1m', '5m', '15m', '1h', '4h', '1d'] as const

export function isOrderTimeframeSupported(timeframe?: string | null): boolean {
  const value = String(timeframe || '').toLowerCase().trim()
  return ORDERABLE_TIMEFRAMES.includes(value as (typeof ORDERABLE_TIMEFRAMES)[number])
}

export function isRollingOrderInterval(interval?: string | null): boolean {
  const value = String(interval || '').toLowerCase().trim()
  return ROLLING_ORDER_INTERVALS.includes(value as RollingOrderInterval)
}
